<template>
    <div class="xls-reader p-4" ref="xlsReaderContainer" :class="{ 'fullscreen-mode': isFullscreen }">
        <button
            v-if="isFullscreen"
            @click="exitFullscreen"
            class="fullscreen-close-btn"
            title="Fermer le plein écran"
        >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"/>
            </svg>
        </button>

        <div class="max-w-7xl mx-auto">
            <!-- Header -->
            <h2 class="style-h1 mb-2">
                {{ title }}
            </h2>

            <!-- Error Alert -->
            <div v-if="error" class="alert alert-error alert-soft mb-6">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>{{ error }}</span>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="card">
                <div class="card-body items-center">
                    <span class="loading loading-spinner loading-lg text-primary"></span>
                    <p class="mt-4 text-lg">Chargement des données...</p>
                </div>
            </div>

            <!-- Data Section -->
            <div v-else-if="data.length" class="space-y-6">
                <!-- Controls -->
                <div class="card">
                    <div class="card-body p-0 mt-2">
                        <div class="flex flex-wrap gap-4 items-center">
                            <!-- Search -->
                            <div class="form-control flex-1">
                                <div class="input-group relative w-full md:max-w-[300px]">
                                    <span class="text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 z-100">
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                        </svg>
                                    </span>
                                    <input
                                        v-model="searchTerm"
                                        type="text"
                                        :placeholder="searchPlaceholder || 'Rechercher...'"
                                        class="input input-bordered flex-1 bg-white w-full"
                                    />
                                </div>
                            </div>

                            <!-- Sheet Selector -->
                            <select
                                v-if="sheets.length > 1 && !hideSheetSelector"
                                v-model="selectedSheet"
                                class="select select-bordered w-full md:w-auto bg-white"
                            >
                                <option v-for="sheet in sheets" :key="sheet" :value="sheet">
                                    {{ sheet }}
                                </option>
                            </select>

                            <!-- Items per page -->
                            <select v-model="itemsPerPage" class="select select-bordered w-full md:w-auto bg-white no-print">
                                <option :value="10">Afficher 10 lignes</option>
                                <option :value="25">Afficher 25 lignes</option>
                                <option :value="50">Afficher 50 lignes</option>
                                <option :value="100">Afficher 100 lignes</option>
                                <option :value="-1">Tout afficher</option>
                            </select>

                            <!-- Actions -->
                            <div class="flex gap-2">
                                <button
                                    v-if="showRefreshButton"
                                    @click="loadData"
                                    class="btn btn-outline btn-info no-print"
                                >
                                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                    </svg>
                                    Actualiser
                                </button>
                            </div>

                            <div class="flex items-center gap-2 px-3 py-2 bg-base-200 rounded-lg text-sm no-print">
                                <span class="text-base-content">
                                    <span class="font-bold text-primary">{{ filteredData.length }}</span>
                                    <span class="mx-1 opacity-70">/</span>
                                    <span class="font-semibold opacity-70">{{ data.length }}</span>
                                    <span class="text-xs ml-1 opacity-70">lignes</span>
                                    <span v-if="searchTerm || selectedColumn" class="badge badge-primary badge-sm ml-2">
                                        {{ filterPercentage }}% filtré
                                    </span>
                                </span>

                                <div class="flex items-center gap-1 ml-2 border-l pl-2 border-base-300">
                                    <button
                                        @click="toggleFullscreen"
                                        class="icon-btn-fullscreen"
                                        title="Plein écran"
                                    >
                                        <span v-if="!isFullscreen" class="icon-mask icon-fullscreen"></span>
                                        <span v-else class="icon-mask icon-fullscreen-exit"></span>
                                    </button>

                                    <button
                                        @click="printTable"
                                        class="icon-btn-print"
                                        title="Imprimer le tableau"
                                    >
                                        <span class="icon-mask icon-print"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div class="card" id="printable-section">
                    <div class="card-body p-0">
                        <!-- Print Header (only visible during print) -->
                        <div class="print-header">
                            <h2>{{ title || 'Rapport de données' }}</h2>
                            <p>Date d'impression : {{ printDate }}</p>
                            <p v-if="selectedSheet && sheets.length > 1">Feuille : {{ selectedSheet }}</p>
                        </div>

                        <div class="overflow-x-auto w-full">
                            <table class="table table-zebra min-w-full" :style="{ tableLayout: 'fixed', width: '100%' }">
                                <thead>
                                <tr>
                                    <th v-if="showRowNumbers" class="sticky left-0 z-10 bg-base-200" style="width: 50px">#</th>
                                    <th
                                        v-for="(col, colIndex) in visibleColumns"
                                        :key="col"
                                        @click="sortBy(col)"
                                        class="cursor-pointer hover:bg-base-200 transition-colors group"
                                        :style="getColumnStyle(colIndex)"
                                    >
                                        <div class="flex items-center gap-1">
                                            {{ props.columnLabels[col] || (col.startsWith('[G] ') ? col.substring(4) : col) }}
                                            <svg
                                                class="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity no-print"
                                                :class="{
                                                    'opacity-100': sortColumn === col,
                                                    'rotate-180': sortColumn === col && sortDirection === 'desc'
                                                }"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                      d="M5 15l7-7 7 7"/>
                                            </svg>
                                        </div>
                                    </th>
                                    <th v-if="$slots.actions" style="width: 100px" class="no-print">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(row, index) in printMode ? filteredData : paginatedData" :key="index" class="hover">
                                    <th v-if="showRowNumbers" class="sticky left-0 bg-base-100" style="width: 50px">
                                        {{ printMode ? index + 1 : (itemsPerPage === -1 ? index + 1 : (currentPage - 1) * itemsPerPage + index + 1) }}
                                    </th>
                                    <td v-for="(col, colIndex) in visibleColumns" :key="col"
                                        :style="getColumnStyle(colIndex)">
                                        <slot :name="`cell-${col}`" :value="row[col]" :row="row">
                                            <!-- Jauge si la colonne est préfixée par [G] -->
                                            <div v-if="col.startsWith('[G] ')" class="gauge-container">
                                                <div
                                                    class="gauge-bar"
                                                    :style="{ width: getGaugePercentage(col, row[col]) + '%' }"
                                                ></div>
                                                <span class="gauge-value">{{ formatNumber(row[col]) }}</span>
                                            </div>
                                            <!-- Image si URL détectée ou notation *[name] -->
                                            <div v-else-if="isImageUrl(row[col], col) && !printMode" class="w-full max-w-[65px]">
                                                <div class="rounded cursor-zoom-in hover:opacity-90 transition-opacity">
                                                    <img
                                                        :src="convertStarNotationToImageUrl(row[col])"
                                                        :alt="`Image ${col}`"
                                                        @click="openPhotoSwipe(convertStarNotationToImageUrl(row[col]), col)"
                                                        @error="handleImageError"
                                                        loading="lazy"
                                                        class="w-full h-auto"
                                                    />
                                                </div>
                                            </div>
                                            <!-- Pour l'impression, afficher l'URL de l'image -->
                                            <span v-else-if="isImageUrl(row[col], col) && printMode" class="text-xs">
                                                [Image: {{ truncateUrl(convertStarNotationToImageUrl(row[col])) }}]
                                            </span>
                                            <!-- Date si détectée -->
                                            <span v-else-if="isDate(row[col])" class="badge badge-ghost badge-sm">
                                                {{ formatDate(row[col]) }}
                                            </span>
                                            <!-- Badge pour nombres -->
                                            <span v-else-if="isNumeric(row[col])" class="badge badge-ghost badge-sm">
                                                {{ formatNumber(row[col]) }}
                                            </span>
                                            <!-- Lien si URL détectée -->
                                            <a
                                                v-else-if="isUrl(row[col])"
                                                :href="row[col]"
                                                target="_blank"
                                                class="link link-primary"
                                            >
                                                {{ truncateUrl(row[col]) }}
                                            </a>
                                            <!-- Texte normal -->
                                            <span v-else>{{ row[col] || '-' }}</span>
                                        </slot>
                                    </td>
                                    <td v-if="$slots.actions" class="no-print">
                                        <slot name="actions" :row="row" :index="index"></slot>
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div v-if="!paginatedData.length && !printMode" class="text-center pb-4">
                                <p class="text-base-content/60">Aucune donnée trouvée</p>
                            </div>
                        </div>

                        <!-- Pagination -->
                        <div class="flex justify-center p-4 no-print" v-if="totalPages > 1 && itemsPerPage !== -1">
                            <ul class="pagination ibexa-pagination__navigation">
                                <!-- Bouton Précédent -->
                                <li :class="['page-item', 'prev', { disabled: currentPage === 1 }]">
                                    <span v-if="currentPage === 1" class="page-link">Précédent</span>
                                    <a v-else class="page-link" href="#" @click.prevent="currentPage--">Précédent</a>
                                </li>

                                <!-- Pages -->
                                <template v-for="page in paginationRange" :key="page">
                                    <!-- Page active -->
                                    <li v-if="page === currentPage" class="page-item active">
                                        <span class="page-link">{{ page }}</span>
                                    </li>
                                    <!-- Ellipsis -->
                                    <li v-else-if="page === '...'" class="page-item disabled">
                                        <span class="page-link">…</span>
                                    </li>
                                    <!-- Autres pages -->
                                    <li v-else class="page-item">
                                        <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                                    </li>
                                </template>

                                <!-- Bouton Suivant -->
                                <li :class="['page-item', 'next', { disabled: currentPage === totalPages }]">
                                    <span v-if="currentPage === totalPages" class="page-link">Suivant</span>
                                    <a v-else class="page-link" href="#" @click.prevent="currentPage++" rel="next">Suivant</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No Data State -->
            <div v-else-if="!isLoading && !error" class="card">
                <div class="card-body items-center">
                    <svg class="w-24 h-24 text-base-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    <p class="text-lg text-base-content/60">Aucune donnée disponible</p>
                </div>
            </div>
        </div>

        <!-- PhotoSwipe Root element -->
        <div ref="pswpElement" class="pswp" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="pswp__bg"></div>
            <div class="pswp__scroll-wrap">
                <div class="pswp__container">
                    <div class="pswp__item"></div>
                    <div class="pswp__item"></div>
                    <div class="pswp__item"></div>
                </div>
                <div class="pswp__ui pswp__ui--hidden">
                    <div class="pswp__top-bar">
                        <div class="pswp__counter"></div>
                        <button class="pswp__button pswp__button--close" title="Close (Esc)"></button>
                        <button class="pswp__button pswp__button--share" title="Share"></button>
                        <button class="pswp__button pswp__button--fs" title="Toggle fullscreen"></button>
                        <button class="pswp__button pswp__button--zoom" title="Zoom in/out"></button>
                        <div class="pswp__preloader">
                            <div class="pswp__preloader__icn">
                                <div class="pswp__preloader__cut">
                                    <div class="pswp__preloader__donut"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="pswp__share-modal pswp__share-modal--hidden pswp__single-tap">
                        <div class="pswp__share-tooltip"></div>
                    </div>
                    <button class="pswp__button pswp__button--arrow--left" title="Previous (arrow left)"></button>
                    <button class="pswp__button pswp__button--arrow--right" title="Next (arrow right)"></button>
                    <div class="pswp__caption">
                        <div class="pswp__caption__center"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

// PhotoSwipe imports
let PhotoSwipe = null
let PhotoSwipeUI_Default = null

// Charger XLSX depuis CDN
let XLSX = null
const xlsxLoaded = ref(false)

// Props
const props = defineProps({
    source: {
        type: [String, File, Array, Object],
        required: true
    },
    sourceType: {
        type: String,
        default: 'auto',
        validator: (value) => ['auto', 'url', 'file', 'json', 'base64'].includes(value)
    },
    defaultSheet: {
        type: String,
        default: null
    },
    visibleColumnsProp: {
        type: Array,
        default: () => []
    },
    columnLabels: {
        type: Object,
        default: () => ({})
    },
    imageColumns: {
        type: Array,
        default: () => []
    },
    autoDetectImages: {
        type: Boolean,
        default: true
    },
    title: String,
    searchPlaceholder: String,
    showRowNumbers: {
        type: Boolean,
        default: false
    },
    showRefreshButton: {
        type: Boolean,
        default: false
    },
    hideSheetSelector: {
        type: Boolean,
        default: false
    },
    expandFirstColumn: {
        type: Boolean,
        default: true
    },
    firstColumnMinWidth: {
        type: String,
        default: '200px'
    },
    autoRefresh: {
        type: Number,
        default: 0
    },
    enableImageZoom: {
        type: Boolean,
        default: true
    }
})

// Emits
const emit = defineEmits(['loaded', 'error', 'filtered', 'selected'])

// State
const data = ref([])
const columns = ref([])
const sheets = ref([])
const selectedSheet = ref('')
const allData = ref({})
const searchTerm = ref('')
const selectedColumn = ref('')
const sortColumn = ref('')
const sortDirection = ref('asc')
const currentPage = ref(1)
const itemsPerPage = ref(50)
const isLoading = ref(false)
const error = ref('')
const detectedImageColumns = ref(new Set())
const pswpElement = ref(null)
const xlsReaderContainer = ref(null)
const isFullscreen = ref(false)
const printMode = ref(false)
const printDate = ref('')
let refreshInterval = null
let gallery = null

// Computed
// Calcule la longueur max de chaque colonne visible
const columnsMaxLength = computed(() => {
    if (visibleColumns.value.length === 0 || data.value.length === 0) return {}
    const lengths = {}
    visibleColumns.value.forEach(col => {
        let maxLen = 0
        data.value.forEach(row => {
            const val = String(row[col] || '')
            if (val.length > maxLen) maxLen = val.length
        })
        lengths[col] = maxLen
    })
    return lengths
})

// Vérifie si une colonne doit avoir une largeur réduite
const isSmallColumn = (col) => {
    // Colonnes images
    if (props.imageColumns.includes(col)) return true
    if (detectedImageColumns.value.has(col)) return true
    // Colonnes jauge
    if (col.startsWith('[G] ')) return true
    // Colonnes avec contenu court (< 20 chars)
    if ((columnsMaxLength.value[col] || 0) < 20) return true
    // Colonnes URLs
    if (data.value.length > 0) {
        const sampleSize = Math.min(5, data.value.length)
        let urlCount = 0
        for (let i = 0; i < sampleSize; i++) {
            const val = data.value[i][col]
            if (isUrl(val) || (typeof val === 'string' && val.startsWith('*'))) {
                urlCount++
            }
        }
        if (urlCount / sampleSize > 0.5) return true
    }
    return false
}

// Calcule les pourcentages de largeur pour chaque colonne
const columnWidths = computed(() => {
    const cols = visibleColumns.value
    if (cols.length === 0) return {}

    const smallCols = cols.filter(col => isSmallColumn(col))
    const largeCols = cols.filter(col => !isSmallColumn(col))

    // Largeur fixe pour les petites colonnes (8% chacune)
    const smallWidth = 8
    const totalSmallWidth = smallCols.length * smallWidth

    // Le reste pour les grandes colonnes, réparti équitablement
    const remainingWidth = 100 - totalSmallWidth
    const largeWidth = largeCols.length > 0 ? remainingWidth / largeCols.length : 0

    const widths = {}
    cols.forEach(col => {
        widths[col] = isSmallColumn(col) ? smallWidth : largeWidth
    })
    return widths
})

const getColumnStyle = (colIndex) => {
    const col = visibleColumns.value[colIndex]
    const width = columnWidths.value[col] || 10
    return { width: `${width}%` }
}

const visibleColumns = computed(() => {
    if (props.visibleColumnsProp && props.visibleColumnsProp.length > 0) {
        return columns.value.filter(col => props.visibleColumnsProp.includes(col))
    }
    return columns.value
})

const filteredData = computed(() => {
    let filtered = [...data.value]

    if (searchTerm.value) {
        const search = searchTerm.value.toLowerCase()
        filtered = filtered.filter(row => {
            if (selectedColumn.value) {
                const cellValue = String(row[selectedColumn.value] || '').toLowerCase()
                return cellValue.includes(search)
            }
            return visibleColumns.value.some(col => {
                const cellValue = String(row[col] || '').toLowerCase()
                return cellValue.includes(search)
            })
        })
    }

    if (sortColumn.value) {
        filtered.sort((a, b) => {
            const aVal = a[sortColumn.value] || ''
            const bVal = b[sortColumn.value] || ''

            // Gérer les dates
            if (isDate(aVal) && isDate(bVal)) {
                const dateA = parseDate(aVal)
                const dateB = parseDate(bVal)
                return sortDirection.value === 'asc'
                    ? dateA - dateB
                    : dateB - dateA
            }

            if (isNumeric(aVal) && isNumeric(bVal)) {
                const numA = parseNumeric(aVal)
                const numB = parseNumeric(bVal)
                return sortDirection.value === 'asc'
                    ? numA - numB
                    : numB - numA
            }

            const comparison = String(aVal).localeCompare(String(bVal))
            return sortDirection.value === 'asc' ? comparison : -comparison
        })
    }

    emit('filtered', filtered)
    return filtered
})

const totalPages = computed(() => {
    if (itemsPerPage.value === -1) return 1
    return Math.ceil(filteredData.value.length / itemsPerPage.value)
})

const paginatedData = computed(() => {
    if (itemsPerPage.value === -1) return filteredData.value
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return filteredData.value.slice(start, end)
})

const filterPercentage = computed(() => {
    if (data.value.length === 0) return 0
    return Math.round((filteredData.value.length / data.value.length) * 100)
})

const paginationRange = computed(() => {
    const range = []
    const total = totalPages.value
    const current = currentPage.value
    const delta = 2
    const rangeWithDots = []
    let l

    range.push(1)

    for (let i = current - delta; i <= current + delta; i++) {
        if (i > 1 && i < total) {
            range.push(i)
        }
    }

    if (total > 1) {
        range.push(total)
    }

    range.forEach((i) => {
        if (l) {
            if (i - l === 2) {
                rangeWithDots.push(l + 1)
            } else if (i - l !== 1) {
                rangeWithDots.push('...')
            }
        }
        rangeWithDots.push(i)
        l = i
    })

    return rangeWithDots
})

// Max values for gauge columns
const columnMaxValues = computed(() => {
    const maxVals = {}
    columns.value.forEach(col => {
        if (col.startsWith('[G] ')) {
            let max = 0
            data.value.forEach(row => {
                const val = parseNumeric(row[col])
                if (!isNaN(val) && val > max) {
                    max = val
                }
            })
            maxVals[col] = max
        }
    })
    return maxVals
})

const getGaugePercentage = (column, value) => {
    const max = columnMaxValues.value[column]
    if (!max || max === 0) return 0
    const val = parseNumeric(value)
    if (isNaN(val)) return 0
    return Math.min(100, Math.max(0, (val / max) * 100))
}


const parseNumeric = (val) => {
    if (val === null || val === undefined || val === '') return 0
    if (typeof val === 'number') return val
    const str = String(val)
    const cleanStr = str.replace(/\s+/g, '').replace(',', '.')
    const parsed = parseFloat(cleanStr)
    return isNaN(parsed) ? 0 : parsed
}

// Helper functions
const convertStarNotationToImageUrl = (value) => {
    if (!value || typeof value !== 'string') return value

    // Détecter le pattern *name et le convertir en /name.png
    const starPattern = /^\*(.+)$/
    const match = value.match(starPattern)

    if (match) {
        const name = match[1]
        return `/assets/images/logos_audience/${name}.png`
    }

    if (value.startsWith('https://sfxcontrib.satellifacts.com/')) {
        return value.replace('https://sfxcontrib.satellifacts.com/', 'https://satellifacts.com/')
    }

    return value
}

const isNumeric = (value) => {
    // Ne pas considérer les dates comme des nombres
    if (isDate(value)) return false
    if (typeof value === 'number') return true
    if (typeof value !== 'string') return false

    const cleanStr = value.replace(/\s+/g, '').replace(',', '.')
    return !isNaN(parseFloat(cleanStr)) && isFinite(cleanStr)
}

const isDate = (value) => {
    if (!value) return false

    // Vérifier si c'est une instance Date
    if (value instanceof Date && !isNaN(value)) return true

    // Vérifier les formats de date communs
    if (typeof value === 'string') {
        // Patterns de dates français et internationaux
        const datePatterns = [
            /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,  // dd/mm/yyyy ou d/m/yy
            /^\d{1,2}-\d{1,2}-\d{2,4}$/,     // dd-mm-yyyy
            /^\d{4}-\d{2}-\d{2}$/,            // yyyy-mm-dd
            /^\d{1,2}\.\d{1,2}\.\d{2,4}$/,   // dd.mm.yyyy
        ]
        return datePatterns.some(pattern => pattern.test(value))
    }

    return false
}

const parseDate = (value) => {
    if (!value) return null

    if (value instanceof Date) return value

    if (typeof value === 'string') {
        // Parser les dates au format dd/mm/yyyy
        const parts = value.split(/[\/\-\.]/)
        if (parts.length === 3) {
            const day = parseInt(parts[0])
            const month = parseInt(parts[1]) - 1
            const year = parseInt(parts[2])
            return new Date(year, month, day)
        }
    }

    // Si c'est un nombre sériel Excel
    if (typeof value === 'number' && value > 25569 && value < 50000) {
        return new Date((value - 25569) * 86400 * 1000)
    }

    return new Date(value)
}

const formatDate = (value) => {
    if (!value) return '-'

    // Si c'est déjà une string formatée correctement
    if (typeof value === 'string' && isDate(value)) {
        return value
    }

    // Si c'est un objet Date
    if (value instanceof Date && !isNaN(value)) {
        const day = String(value.getDate()).padStart(2, '0')
        const month = String(value.getMonth() + 1).padStart(2, '0')
        const year = value.getFullYear()
        return `${day}/${month}/${year}`
    }

    // Si c'est un nombre sériel Excel (nombre de jours depuis 1900)
    if (typeof value === 'number' && value > 25569 && value < 50000) {
        const date = new Date((value - 25569) * 86400 * 1000)
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
    }

    return value
}

const formatNumber = (value) => {
    const num = parseNumeric(value)
    if (Number.isInteger(num)) {
        return num.toLocaleString('fr-FR')
    }
    return num.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
}

const isUrl = (value) => {
    if (!value || typeof value !== 'string') return false
    try {
        const pattern = /^(https?:\/\/|www\.)/i
        return pattern.test(value)
    } catch {
        return false
    }
}

const isImageUrl = (value, columnName) => {
    if (!value || typeof value !== 'string') return false

    // Vérifier d'abord si c'est la notation *name
    const starPattern = /^\*(.+)$/
    if (starPattern.test(value)) {
        return true
    }

    if (props.imageColumns.includes(columnName)) {
        return isUrl(value)
    }

    if (props.autoDetectImages) {
        if (detectedImageColumns.value.has(columnName)) {
            return isUrl(value) // Seulement si c'est une vraie URL
        }

        if (isUrl(value)) {
            const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?.*)?$/i
            const imageInPath = /\/(jpg|jpeg|png|gif|webp|svg|bmp|ico|image)[\/-]/i
            const imageServices = /(unsplash|pexels|imgur|cloudinary|amazonaws|googleusercontent|gravatar|github.*\/(blob|raw)|picsum|placeholder|cfnews\.net.*\/images)/i
            const imageKeywords = /\/(image|images|img|photo|photos|picture|pictures|media|upload|uploads|storage|assets|static|content|files)[\/-]/i

            return imageExtensions.test(value) ||
                imageInPath.test(value) ||
                imageServices.test(value) ||
                imageKeywords.test(value)
        }
    }

    return false
}

const truncateUrl = (url) => {
    if (!url) return ''
    const maxLength = 30
    if (url.length <= maxLength) return url
    return url.substring(0, maxLength) + '...'
}

const handleImageError = (e) => {
    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f0f0f0"/%3E%3Ctext x="50" y="50" font-family="Arial" font-size="14" fill="%23999" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E'
}


const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
        try {
            await xlsReaderContainer.value.requestFullscreen()
            isFullscreen.value = true
        } catch (err) {
            console.error('Erreur lors du passage en plein écran:', err)
        }
    } else {
        await exitFullscreen()
    }
}

const exitFullscreen = async () => {
    if (document.fullscreenElement) {
        try {
            await document.exitFullscreen()
            isFullscreen.value = false
        } catch (err) {
            console.error('Erreur lors de la sortie du plein écran:', err)
        }
    }
}

const printTable = () => {
    // Créer une copie des données pour l'impression
    const printContent = document.createElement('div')

    // En-tête d'impression
    const header = document.createElement('div')
    header.className = 'print-header'
    header.innerHTML = `
        <h2>${props.title || 'Rapport de données'}</h2>
        <p>Date: ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
        ${selectedSheet.value && sheets.value.length > 1 ? `<p>Feuille: ${selectedSheet.value}</p>` : ''}
    `

    // Créer le tableau
    const table = document.createElement('table')
    table.style.width = '100%'
    table.style.borderCollapse = 'collapse'

    // En-tête du tableau
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')

    visibleColumns.value.forEach(col => {
        const th = document.createElement('th')
        th.textContent = props.columnLabels[col] || (col.startsWith('[G] ') ? col.substring(4) : col)
        th.style.border = '1px solid #ddd'
        th.style.padding = '8px'
        th.style.backgroundColor = '#f5f5f5'
        th.style.textAlign = 'left'
        headerRow.appendChild(th)
    })

    thead.appendChild(headerRow)
    table.appendChild(thead)

    // Corps du tableau
    const tbody = document.createElement('tbody')

    // Utiliser toutes les données filtrées pour l'impression
    filteredData.value.forEach((row, index) => {
        const tr = document.createElement('tr')

        visibleColumns.value.forEach(col => {
            const td = document.createElement('td')
            td.style.border = '1px solid #ddd'
            td.style.padding = '8px'

            const value = row[col]

            // Gérer les jauges
            if (col.startsWith('[G] ')) {
                const percentage = getGaugePercentage(col, value)
                td.innerHTML = `
                    <div style="position: relative; width: 100%; height: 20px; background: #eee; border-radius: 4px; overflow: hidden;">
                        <div style="width: ${percentage}%; height: 100%; background: #bf2923;"></div>
                        <span style="position: absolute; width: 100%; text-align: center; top: 0; font-size: 10px; line-height: 20px; color: ${percentage > 50 ? 'white' : 'black'};">${value}</span>
                    </div>
                `
            }
            // Gérer les images
            else if (isImageUrl(value, col)) {
                const imgSrc = convertStarNotationToImageUrl(value)
                const img = document.createElement('img')
                img.src = imgSrc
                img.style.maxWidth = '80px'
                img.style.maxHeight = '80px'
                img.style.display = 'block'
                td.appendChild(img)
            }
            // Gérer les dates
            else if (isDate(value)) {
                td.textContent = formatDate(value)
            }
            // Gérer les nombres
            else if (isNumeric(value)) {
                td.textContent = formatNumber(value)
            }
            // Gérer les URLs
            else if (isUrl(value)) {
                td.textContent = value
                td.style.color = 'blue'
            }
            // Texte normal
            else {
                td.textContent = value || '-'
            }

            tr.appendChild(td)
        })

        tbody.appendChild(tr)
    })

    table.appendChild(tbody)

    // Assembler le contenu
    printContent.appendChild(header)
    printContent.appendChild(table)

    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank', 'width=1200,height=800')

    // HTML de la page d'impression
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${props.title || 'Impression'}</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    margin: 20px;
                }
                .print-header {
                    text-align: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 3px solid #333;
                }
                .print-header h2 {
                    margin: 0 0 10px 0;
                    color: #333;
                }
                .print-header p {
                    margin: 5px 0;
                    color: #666;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                }
                th {
                    background-color: #f5f5f5;
                    font-weight: bold;
                    text-align: left;
                    padding: 10px;
                    border: 1px solid #ddd;
                }
                td {
                    padding: 8px;
                    border: 1px solid #ddd;
                }
                tr:nth-child(even) {
                    background-color: #f9f9f9;
                }
                img {
                    max-width: 80px;
                    max-height: 80px;
                    display: block;
                }
                @media print {
                    @page {
                        size: A4 landscape;
                        margin: 1cm;
                    }
                }
            </style>
        </head>
        <body>
            ${printContent.innerHTML}
        </body>
        </html>
    `)

    printWindow.document.close()

    // Attendre le chargement puis imprimer
    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.print()
            printWindow.close()
        }, 500)
    }
}

// PhotoSwipe Methods
const loadPhotoSwipe = async () => {
    if (window.PhotoSwipe && window.PhotoSwipeUI_Default) {
        PhotoSwipe = window.PhotoSwipe
        PhotoSwipeUI_Default = window.PhotoSwipeUI_Default
        return
    }

    // Charger PhotoSwipe CSS si pas déjà présent
    if (!document.querySelector('link[href*="photoswipe.css"]')) {
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe.min.css'
        document.head.appendChild(cssLink)

        const skinLink = document.createElement('link')
        skinLink.rel = 'stylesheet'
        skinLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/default-skin/default-skin.min.css'
        document.head.appendChild(skinLink)
    }

    // Charger PhotoSwipe JS
    return new Promise((resolve, reject) => {
        const loadScript = (src) => {
            return new Promise((resolveScript, rejectScript) => {
                const script = document.createElement('script')
                script.src = src
                script.onload = resolveScript
                script.onerror = rejectScript
                document.head.appendChild(script)
            })
        }

        Promise.all([
            loadScript('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe.min.js'),
            loadScript('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe-ui-default.min.js')
        ]).then(() => {
            PhotoSwipe = window.PhotoSwipe
            PhotoSwipeUI_Default = window.PhotoSwipeUI_Default
            resolve()
        }).catch(reject)
    })
}

const openPhotoSwipe = async (imageUrl, columnName) => {
    if (!props.enableImageZoom) return

    // Charger PhotoSwipe si nécessaire
    if (!PhotoSwipe) {
        await loadPhotoSwipe()
    }

    // Collecter toutes les images de la page actuelle
    const images = []
    let currentIndex = 0

    paginatedData.value.forEach((row, rowIndex) => {
        visibleColumns.value.forEach(col => {
            const cellValue = convertStarNotationToImageUrl(row[col])
            if (isImageUrl(row[col], col)) {
                if (cellValue === imageUrl && col === columnName) {
                    currentIndex = images.length
                }
                images.push({
                    src: cellValue,
                    w: 0, // Sera calculé dynamiquement
                    h: 0,
                    title: `${props.columnLabels[col] || col} - Ligne ${(currentPage.value - 1) * itemsPerPage.value + rowIndex + 1}`
                })
            }
        })
    })

    // Si aucune image trouvée, ouvrir juste l'image cliquée
    if (images.length === 0) {
        images.push({
            src: imageUrl,
            w: 0,
            h: 0,
            title: props.columnLabels[columnName] || columnName
        })
    }

    // Options PhotoSwipe
    const options = {
        index: currentIndex,
        bgOpacity: 0.9,
        showHideOpacity: true,
        closeOnScroll: false,
        history: false,
        shareEl: false,
        fullscreenEl: true,
        zoomEl: true,
        getThumbBoundsFn: false
    }

    // Attendre le prochain tick pour s'assurer que l'élément est dans le DOM
    await nextTick()

    // Créer et ouvrir PhotoSwipe
    gallery = new PhotoSwipe(pswpElement.value, PhotoSwipeUI_Default, images, options)

    // Calculer les dimensions réelles des images au chargement
    gallery.listen('gettingData', function(index, item) {
        if (item.w < 1 || item.h < 1) {
            const img = new Image()
            img.onload = function() {
                item.w = this.width
                item.h = this.height
                gallery.invalidateCurrItems()
                gallery.updateSize(true)
            }
            img.src = item.src
        }
    })

    gallery.init()
}

// Methods
const loadXLSX = async () => {
    if (window.XLSX) {
        XLSX = window.XLSX
        xlsxLoaded.value = true
        return
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js'
        script.onload = () => {
            XLSX = window.XLSX
            xlsxLoaded.value = true
            resolve()
        }
        script.onerror = reject
        document.head.appendChild(script)
    })
}

const detectSourceType = () => {
    if (props.sourceType !== 'auto') return props.sourceType

    const source = props.source

    if (typeof source === 'string') {
        if (source.startsWith('http://') || source.startsWith('https://') || source.startsWith('/')) {
            return 'url'
        }
        if (source.includes('base64,')) {
            return 'base64'
        }
        try {
            JSON.parse(source)
            return 'json'
        } catch {
            return 'base64'
        }
    }

    if (source instanceof File) {
        return 'file'
    }

    if (Array.isArray(source)) {
        return 'json'
    }

    if (typeof source === 'object') {
        return 'json'
    }

    return 'unknown'
}

const detectImageColumns = (sheetData) => {
    if (!props.autoDetectImages || sheetData.length === 0) return

    const detected = new Set()
    const sampleSize = Math.min(10, sheetData.length)

    columns.value.forEach(col => {
        let imageCount = 0

        for (let i = 0; i < sampleSize; i++) {
            const value = sheetData[i][col]
            if (value && typeof value === 'string') {
                // Détecter la notation *name
                const starPattern = /^\*(.+)$/
                if (starPattern.test(value)) {
                    imageCount++
                    continue
                }

                const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)(\?.*)?$/i
                const imageInPath = /\/(jpg|jpeg|png|gif|webp|svg|bmp|ico|image)[\/-]/i
                const imageServices = /(unsplash|pexels|imgur|cloudinary|amazonaws|googleusercontent|gravatar|github.*\/(blob|raw)|picsum|placeholder|cfnews)/i
                const imageKeywords = /\/(image|images|img|photo|photos|picture|pictures|media|upload|uploads|storage|assets|static|content|files)[\/-]/i
                const dataUri = /^data:image\//i
                const columnNamePattern = /(image|img|photo|picture|avatar|thumbnail|thumb|logo|icon|banner|media|illustration)/i

                if (isUrl(value)) {
                    if (imageExtensions.test(value) ||
                        imageInPath.test(value) ||
                        imageServices.test(value) ||
                        imageKeywords.test(value) ||
                        dataUri.test(value)) {
                        imageCount++
                    }
                } else if (dataUri.test(value)) {
                    imageCount++
                }

                if (columnNamePattern.test(col)) {
                    imageCount += 0.5
                }
            }
        }

        if (imageCount / sampleSize > 0.3) {
            detected.add(col)
        }
    })

    detectedImageColumns.value = detected
}

const loadData = async () => {
    if (!xlsxLoaded.value) {
        await loadXLSX()
    }

    isLoading.value = true
    error.value = ''

    try {
        const type = detectSourceType()

        switch (type) {
            case 'url':
                await loadFromUrl(props.source)
                break
            case 'file':
                await loadFromFile(props.source)
                break
            case 'json':
                loadFromJson(props.source)
                break
            case 'base64':
                await loadFromBase64(props.source)
                break
            default:
                throw new Error('Type de source non supporté')
        }

        emit('loaded', { data: data.value, columns: columns.value, sheets: sheets.value })
    } catch (err) {
        error.value = `Erreur lors du chargement: ${err.message}`
        emit('error', err)
        console.error('Erreur de chargement:', err)
    } finally {
        isLoading.value = false
    }
}

const loadFromUrl = async (url) => {
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    processExcelData(arrayBuffer)
}

const loadFromFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer()
    processExcelData(arrayBuffer)
}

const loadFromJson = (jsonData) => {
    if (Array.isArray(jsonData)) {
        data.value = jsonData
        if (jsonData.length > 0) {
            columns.value = Object.keys(jsonData[0])
            detectImageColumns(jsonData)
        }
        sheets.value = ['Data']
        selectedSheet.value = 'Data'
        allData.value = { 'Data': jsonData }
    } else if (typeof jsonData === 'object') {
        sheets.value = Object.keys(jsonData)
        allData.value = jsonData
        selectedSheet.value = props.defaultSheet || sheets.value[0]
        loadSheetData(selectedSheet.value)
    }
}

const loadFromBase64 = async (base64String) => {
    const base64Data = base64String.includes('base64,')
        ? base64String.split('base64,')[1]
        : base64String

    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }
    processExcelData(bytes.buffer)
}

const processExcelData = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, {
        type: 'array',
        cellDates: true,  // Convertir automatiquement les dates
        dateNF: 'dd/mm/yyyy'  // Format de date par défaut
    })

    sheets.value = workbook.SheetNames
    allData.value = {}

    workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            raw: true,  // Garder les valeurs brutes (nombres, dates)
            dateNF: 'dd/mm/yyyy'  // Format de date
        })
        allData.value[sheetName] = jsonData
    })

    selectedSheet.value = props.defaultSheet || sheets.value[0]
    loadSheetData(selectedSheet.value)
}

const loadSheetData = (sheetName) => {
    const sheetData = allData.value[sheetName] || []
    data.value = sheetData

    if (sheetData.length > 0) {
        columns.value = Object.keys(sheetData[0])
        detectImageColumns(sheetData)
    } else {
        columns.value = []
        detectedImageColumns.value = new Set()
    }

    currentPage.value = 1
    sortColumn.value = ''
    sortDirection.value = 'asc'
}

const sortBy = (column) => {
    if (sortColumn.value === column) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
        sortColumn.value = column
        sortDirection.value = 'asc'
    }
}

// Watchers
watch(() => props.source, () => {
    loadData()
})

watch(selectedSheet, (newSheet) => {
    if (newSheet) {
        loadSheetData(newSheet)
    }
})

watch([searchTerm, selectedColumn], () => {
    currentPage.value = 1
})

// Lifecycle
onMounted(async () => {
    await loadXLSX()

    // Charger PhotoSwipe si le zoom est activé
    if (props.enableImageZoom) {
        loadPhotoSwipe()
    }

    loadData()

    if (props.autoRefresh > 0) {
        refreshInterval = setInterval(() => {
            loadData()
        }, props.autoRefresh)
    }

    // Écouter les changements d'état fullscreen
    document.addEventListener('fullscreenchange', () => {
        isFullscreen.value = !!document.fullscreenElement
    })
})

onUnmounted(() => {
    if (refreshInterval) {
        clearInterval(refreshInterval)
    }

    // Nettoyer PhotoSwipe si ouvert
    if (gallery && typeof gallery.destroy === 'function') {
        gallery.destroy()
    }
})

// Expose
defineExpose({
    refresh: loadData,
    getData: () => data.value,
    getFilteredData: () => filteredData.value,
    print: printTable,
    toggleFullscreen
})
</script>

<style scoped>
.xls-reader {
    .table {
        width: 100% !important;
        tbody {
            display: revert;
        }
    }

    .overflow-x-auto {
        width: 100%;
    }

    /* Style pour la première colonne extensible */
    .table th:nth-child(2),
    .table td:nth-child(2) {
        width: auto;
        min-width: 90px;
    }

    .table th:not(:nth-child(1)):not(:nth-child(2)),
    .table td:not(:nth-child(1)):not(:nth-child(2)) {
        width: auto;
    }

    .table {
        :where(th,td) {
            padding-block: .25rem;
            padding-inline: .25rem;
            vertical-align: middle;
        }
        :where(th) {
            font-size: 0.65rem;
        }
        :where(td) {
            font-size: 0.75rem;
            max-width: 120px;
            word-wrap: break-word;
        }
    }

    /* Styles pour le curseur sur les images */
    .cursor-zoom-in {
        cursor: zoom-in;
    }

    /* Style pour améliorer l'apparence des images */
    img {
        display: block;
        max-width: 100%;
        height: auto;
        border-radius: 0.25rem;
    }

    /* Styles pour le mode plein écran */
    &.fullscreen-mode {
        background: white;
        padding: 1rem;
        z-index: 9999;
        overflow: auto;
        position: relative;

        .max-w-7xl {
            max-width: 100%;
        }
    }

    /* Bouton de fermeture du plein écran */
    .fullscreen-close-btn {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        background: white;
        color: var(--color-gray-500);
        border: none;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    /* Styles pour les icônes personnalisées */
    .icon-mask {
        display: inline-block;
        width: 18px;
        height: 18px;
        background-color: currentColor;
    }

    .icon-print {
        mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M16.771 5.625h-1.146v-2.5A.625.625 0 0 0 15 2.5H5a.625.625 0 0 0-.625.625v2.5H3.229c-1.091 0-1.979.841-1.979 1.875v6.25a.625.625 0 0 0 .625.625h2.5v2.5A.625.625 0 0 0 5 17.5h10a.624.624 0 0 0 .625-.625v-2.5h2.5a.624.624 0 0 0 .625-.625V7.5c0-1.034-.887-1.875-1.979-1.875M5.625 3.75h8.75v1.875h-8.75zm8.75 12.5h-8.75V12.5h8.75zm3.125-3.125h-1.875v-1.25A.624.624 0 0 0 15 11.25H5a.625.625 0 0 0-.625.625v1.25H2.5V7.5c0-.345.327-.625.729-.625H16.77c.402 0 .729.28.729.625zm-1.875-4.062a.938.938 0 1 1-1.875 0 .938.938 0 0 1 1.875 0"/></svg>') no-repeat center / contain;
        -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M16.771 5.625h-1.146v-2.5A.625.625 0 0 0 15 2.5H5a.625.625 0 0 0-.625.625v2.5H3.229c-1.091 0-1.979.841-1.979 1.875v6.25a.625.625 0 0 0 .625.625h2.5v2.5A.625.625 0 0 0 5 17.5h10a.624.624 0 0 0 .625-.625v-2.5h2.5a.624.624 0 0 0 .625-.625V7.5c0-1.034-.887-1.875-1.979-1.875M5.625 3.75h8.75v1.875h-8.75zm8.75 12.5h-8.75V12.5h8.75zm3.125-3.125h-1.875v-1.25A.624.624 0 0 0 15 11.25H5a.625.625 0 0 0-.625.625v1.25H2.5V7.5c0-.345.327-.625.729-.625H16.77c.402 0 .729.28.729.625zm-1.875-4.062a.938.938 0 1 1-1.875 0 .938.938 0 0 1 1.875 0"/></svg>') no-repeat center / contain;
    }

    .icon-fullscreen {
        mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M16.875 3.75V7.5a.625.625 0 1 1-1.25 0V5.259l-3.308 3.308a.625.625 0 0 1-.884-.884l3.308-3.308H12.5a.625.625 0 1 1 0-1.25h3.75a.625.625 0 0 1 .625.625m-9.192 7.683L4.375 14.74V12.5a.625.625 0 1 0-1.25 0v3.75a.625.625 0 0 0 .625.625H7.5a.625.625 0 1 0 0-1.25H5.259l3.308-3.308a.625.625 0 0 0-.884-.884m8.567.442a.624.624 0 0 0-.625.625v2.241l-3.308-3.308a.625.625 0 0 0-.884.884l3.308 3.308H12.5a.624.624 0 1 0 0 1.25h3.75a.624.624 0 0 0 .625-.625V12.5a.624.624 0 0 0-.625-.625m-10.991-7.5H7.5a.625.625 0 0 0 0-1.25H3.75a.625.625 0 0 0-.625.625V7.5a.625.625 0 0 0 1.25 0V5.259l3.308 3.308a.625.625 0 0 0 .884-.884z"/></svg>') no-repeat center / contain;
        -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M16.875 3.75V7.5a.625.625 0 1 1-1.25 0V5.259l-3.308 3.308a.625.625 0 0 1-.884-.884l3.308-3.308H12.5a.625.625 0 1 1 0-1.25h3.75a.625.625 0 0 1 .625.625m-9.192 7.683L4.375 14.74V12.5a.625.625 0 1 0-1.25 0v3.75a.625.625 0 0 0 .625.625H7.5a.625.625 0 1 0 0-1.25H5.259l3.308-3.308a.625.625 0 0 0-.884-.884m8.567.442a.624.624 0 0 0-.625.625v2.241l-3.308-3.308a.625.625 0 0 0-.884.884l3.308 3.308H12.5a.624.624 0 1 0 0 1.25h3.75a.624.624 0 0 0 .625-.625V12.5a.624.624 0 0 0-.625-.625m-10.991-7.5H7.5a.625.625 0 0 0 0-1.25H3.75a.625.625 0 0 0-.625.625V7.5a.625.625 0 0 0 1.25 0V5.259l3.308 3.308a.625.625 0 0 0 .884-.884z"/></svg>') no-repeat center / contain;
    }

    .icon-fullscreen-exit {
        mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M7.5 3.125a.625.625 0 1 1 0 1.25H5.259l3.308 3.308a.625.625 0 0 1-.884.884L4.375 5.259V7.5a.625.625 0 1 1-1.25 0V3.75a.625.625 0 0 1 .625-.625zm5 13.75a.625.625 0 1 1 0-1.25h2.241l-3.308-3.308a.625.625 0 0 1 .884-.884l3.308 3.308V12.5a.625.625 0 1 1 1.25 0v3.75a.625.625 0 0 1-.625.625zm0-13.75h3.75a.625.625 0 0 1 .625.625V7.5a.625.625 0 1 1-1.25 0V5.259l-3.308 3.308a.625.625 0 0 1-.884-.884l3.308-3.308H12.5a.625.625 0 1 1 0-1.25M4.375 14.741l3.308-3.308a.625.625 0 0 0-.884-.884l-3.308 3.308V12.5a.625.625 0 1 0-1.25 0v3.75a.625.625 0 0 0 .625.625H7.5a.625.625 0 1 0 0-1.25H5.259l3.308-3.308z"/></svg>') no-repeat center / contain;
        -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentColor" d="M7.5 3.125a.625.625 0 1 1 0 1.25H5.259l3.308 3.308a.625.625 0 0 1-.884.884L4.375 5.259V7.5a.625.625 0 1 1-1.25 0V3.75a.625.625 0 0 1 .625-.625zm5 13.75a.625.625 0 1 1 0-1.25h2.241l-3.308-3.308a.625.625 0 0 1 .884-.884l3.308 3.308V12.5a.625.625 0 1 1 1.25 0v3.75a.625.625 0 0 1-.625.625zm0-13.75h3.75a.625.625 0 0 1 .625.625V7.5a.625.625 0 1 1-1.25 0V5.259l-3.308 3.308a.625.625 0 0 1-.884-.884l3.308-3.308H12.5a.625.625 0 1 1 0-1.25M4.375 14.741l3.308-3.308a.625.625 0 0 0-.884-.884l-3.308 3.308V12.5a.625.625 0 1 0-1.25 0v3.75a.625.625 0 0 0 .625.625H7.5a.625.625 0 1 0 0-1.25H5.259l3.308-3.308z"/></svg>') no-repeat center / contain;
    }

    /* Boutons d'icônes discrets */
    .icon-btn-print,
    .icon-btn-fullscreen {
        padding: 0.25rem;
        background: transparent;
        border: none;
        cursor: pointer;
        opacity: 0.6;
        transition: all 0.2s ease;
        line-height: 10px;
        color: inherit;
        border-radius: 0.25rem;
        &:hover {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.05);
        }

        &:active {
            transform: scale(0.95);
        }
    }

    .gauge-container {
        position: relative;
        width: 100%;
        min-width: 80px;
        height: 18px;
        background-color: oklch(0.73 0.01 0);
        border-radius: 9999px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
    }

    .gauge-bar {
        height: 100%;
        background: var(--color-primary);
        transition: width 0.5s ease-out;
        border-radius: 9999px;
    }

    .gauge-value {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.65rem;
        font-weight: 700;
        color: #fff;
        pointer-events: none;
    }
}

/* Cacher l'en-tête d'impression en dehors du mode impression */
.print-header {
    display: none;
}

/* Styles d'impression - uniquement appliqués lors de l'impression via @media print */
@media print {
    /* Cette section n'est utilisée que si l'utilisateur fait Ctrl+P directement */
    .no-print {
        display: none !important;
    }

    .print-header {
        display: block !important;
    }

    .gauge-container {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .gauge-bar {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}
</style>
